// Verstuurt een aanvraag uit de offertewizard naar offerte-software.com.
// Loopt via de server zodat de sleutel (ELVION_SITE_WEBHOOK_SECRET) geheim blijft.
// Eén verzending per klik; nooit automatisch opnieuw proberen.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { QUOTE_FORWARDING } from "@/lib/quote-forwarding";
import { normalizePhone } from "@/lib/phone-verification.functions";

const opt = (max: number) => z.string().max(max).nullable().optional();
const schema = z.object({
  product: opt(300),
  postcode: z.string().min(1).max(10),
  plaats: z.string().min(1).max(120),
  straat: z.string().min(1).max(200),
  huisnummer: opt(20),
  type_opdracht: opt(100),
  soort_aanvraag: opt(40),
  uitvoerdatum: opt(60),
  intentie: opt(120),
  omschrijving: opt(5000),
  voornaam: z.string().min(1).max(100),
  achternaam: z.string().min(1).max(100),
  telefoon: z.string().min(1).max(30),
  email: z.string().email().max(200),
  fotos: z.array(z.object({ name: z.string().max(200), url: z.string().max(8_000_000) })).max(4).default([]),
  ai_samenvatting: opt(3000),
  ai_categorie: opt(200),
  ai_aandachtspunten: z.array(z.string().max(500)).max(20).default([]),
});

export const verstuurOfferteAanvraag = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => schema.parse(data))
  .handler(async ({ data }) => {
    const token = process.env["ELVION_SITE_WEBHOOK_SECRET"];
    if (!token) throw new Error("De aanvraagservice is tijdelijk niet beschikbaar.");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Alleen aanvragen met een recent geverifieerd telefoonnummer.
    const phone = normalizePhone(data.telefoon);
    const since = new Date(Date.now() - 30 * 60 * 1000).toISOString();
    const { data: verified } = await supabaseAdmin
      .from("phone_verifications").select("verified_at")
      .eq("phone", phone).eq("status", "verified").gte("verified_at", since).limit(1);
    if (!verified?.length) throw new Error("Verifieer eerst je telefoonnummer.");

    // Foto's opslaan zodat de serviceplanner ze via een link kan openen.
    const fotoUrls: string[] = [];
    for (const foto of data.fotos) {
      const m = /^data:(image\/[a-z+.-]+);base64,(.+)$/i.exec(foto.url);
      if (!m) continue;
      const bytes = Uint8Array.from(atob(m[2]), (c) => c.charCodeAt(0));
      if (bytes.length > 10 * 1024 * 1024) continue;
      const ext = m[1].split("/")[1].replace(/[^a-z]/g, "") || "jpg";
      const path = `wizard/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabaseAdmin.storage.from("quote-photos").upload(path, bytes, { contentType: m[1] });
      if (!error) fotoUrls.push(supabaseAdmin.storage.from("quote-photos").getPublicUrl(path).data.publicUrl);
    }

    const naam = `${data.voornaam} ${data.achternaam}`.trim();
    const omschrijving = [
      data.omschrijving?.trim() || null,
      [
        data.product ? `Product: ${data.product}` : null,
        data.type_opdracht ? `Type opdracht: ${data.type_opdracht}` : null,
        data.soort_aanvraag ? `Soort aanvraag: ${data.soort_aanvraag}` : null,
        data.uitvoerdatum ? `Uitvoerdatum: ${data.uitvoerdatum}` : null,
        data.intentie ? `Intentie: ${data.intentie}` : null,
      ].filter(Boolean).join("\n"),
      data.ai_samenvatting ? `AI-samenvatting: ${data.ai_samenvatting}` : null,
    ].filter(Boolean).join("\n\n");

    const body = {
      bron: "elvion-group",
      klant: {
        naam, voornaam: data.voornaam, achternaam: data.achternaam,
        email: data.email, telefoon: phone, postcode: data.postcode, plaats: data.plaats,
        adres: [data.straat, data.huisnummer].filter(Boolean).join(" "),
        straat: data.straat, huisnummer: data.huisnummer ?? undefined,
      },
      onderwerp: data.product || data.type_opdracht || "Offerteaanvraag via website",
      omschrijving,
      product: data.product ?? null,
      type_opdracht: data.type_opdracht ?? null,
      soort_aanvraag: data.soort_aanvraag ?? null,
      uitvoerdatum: data.uitvoerdatum ?? null,
      intentie: data.intentie ?? null,
      foto_urls: fotoUrls,
      telefoon_geverifieerd: true,
      ai_categorie: data.ai_categorie ?? null,
      ai_aandachtspunten: data.ai_aandachtspunten,
    };

    let status = 0;
    let text = "";
    try {
      const res = await fetch(QUOTE_FORWARDING.webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-webhook-token": token },
        body: JSON.stringify(body),
      });
      status = res.status;
      text = await res.text().catch(() => "");
    } catch (err) {
      text = err instanceof Error ? err.message : String(err);
    }
    const ok = status >= 200 && status < 300;
    let json: Record<string, unknown> = {};
    try { json = JSON.parse(text); } catch { /* geen JSON */ }

    await supabaseAdmin.from("quote_forward_log").insert({
      request_id: null, name: naam, email: data.email, phone,
      service: (data.type_opdracht || "offerte").slice(0, 40), target: "webhook",
      status: ok ? "ok" : "failed", status_code: status || null,
      error: ok ? null : (text || "Geen antwoord").slice(0, 1000), payload: body as never,
    });

    if (!ok) {
      console.error("[offerte-aanvraag] offerte-software gaf", status, text.slice(0, 300));
      throw new Error(status === 401 || status === 403
        ? "De aanvraagservice is tijdelijk niet beschikbaar."
        : "Je aanvraag kon niet worden verstuurd. Probeer het later opnieuw of bel ons.");
    }
    const id = String(json.id ?? json.aanvraag_id ?? json.leadId ?? crypto.randomUUID());
    return { ok: true as const, id };
  });
